import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Lock } from "lucide-react";
import api, { formatApiError } from "@/services/api";
import { useLang } from "@/context/LanguageContext";
import { useAgentSession } from "@/context/AgentContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import WhatsAppAssistButton from "@/components/WhatsAppAssistButton";

export default function AgentLogin() {
  const { t } = useLang();
  const { agent, setAgent } = useAgentSession();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (agent) navigate("/agent/dashboard", { replace: true }); }, [agent, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    const cleanPin = pin.replace(/\D/g, "");
    if (cleanPin.length !== 4) {
      toast.error("PIN la dwe gen 4 chif");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/agents/lookup", { phone: phone.trim(), pin: cleanPin });
      setAgent(data);
      toast.success(`Byenveni ${data.name}!`);
      navigate("/agent/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-brand-gold/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-brand-blue/15 blur-3xl" />
      </div>
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo size={56} /></div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-2xl">
          <h1 className="font-heading text-2xl font-bold">{t("agent_login_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("agent_login_sub")}</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="phone">{t("phone")}</Label>
              <div className="mt-1.5 relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="phone" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+509 3000 0000" data-testid="agent-phone-input" className="pl-9 h-11" />
              </div>
            </div>
            <div>
              <Label htmlFor="pin">PIN 4 chif</Label>
              <div className="mt-1.5 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="pin"
                  required
                  inputMode="numeric"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="••••"
                  data-testid="agent-pin-input"
                  className="pl-9 h-11 tracking-[0.35em] font-mono"
                  autoComplete="one-time-code"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Admin ou a ba ou PIN sa a lè yo kreye kont ou.</p>
            </div>
            <Button type="submit" disabled={loading} data-testid="agent-login-btn" className="w-full h-11 bg-brand-blue hover:bg-brand-blueHover text-white font-semibold">
              {loading ? t("loading") : t("login")}
            </Button>
          </form>
          <div className="mt-6 text-center text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">← Retounen</Link>
          </div>
        </div>
      </motion.div>
      <WhatsAppAssistButton />
    </div>
  );
}
